import React, { FC } from 'react';
import { FlexCol, BasicContainer, FlexRow, BodyTextSmall, FlexDynamic, TextDynamic } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { BrandName, TrustedBrands } from '@minecraft.molecules';
import { appConfig } from '@minecraft.app-config';
import { useTranslation, Trans } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import { TestimonialCard } from './PlanSelection/TestimonialCard';

type PlatformUpsellProps = {
  location?: 'register' | 'verify' | 'profile';
  hideInMobile?: boolean;
  title?: string;
  hideTestimonial?: boolean;
  showBrands?: BrandName[];
  testimonial?: {
    image?: string;
    author?: string;
    message?: string;
    memberSince?: string;
  };
};

export const PlatformUpsell: FC<PlatformUpsellProps> = ({
  location = 'register',
  testimonial,
  hideTestimonial,
  title,
  children,
  hideInMobile = true,
  showBrands,
}) => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();

  return (
    <FlexCol
      w="full"
      data-testid="platform-upsell"
      justifyContent="center"
      alignItems="center"
      hidden={hideInMobile}
      h="full"
      mdProps={{ hidden: false }}
      backgroundImage={{
        image: `${appConfig.cdnRoot}/images/static/join-bg.jpeg`,
        size: 'cover',
        position: 'center',
        repeat: 'no-repeat',
      }}
    >
      <FlexDynamic
        w="full"
        justifyContent="center"
        alignItems="start"
        bgColor="maxContrast"
        bgOpacity={0.8}
        zIndex={4}
        className="cn_atom_px-4"
        mdProps={{ className: 'cn_atom_px-6' }}
      >
        <FlexDynamic
          direction="column"
          alignItems="center"
          justifyContent="center"
          w="full"
          className="cn_atom_pb-0"
          maxW="md"
          zIndex={1}
          gap="5"
          mt="6"
          mdProps={{
            gap: '12',
            className: 'cn_atom_py-8',
          }}
        >
          <BasicContainer w="full" className="cn_atom_pr-8" mdProps={{ className: '' }}>
            <TextDynamic
              as="h3"
              fontSize="h3"
              mdProps={{ as: 'h2', fontSize: 'h2', align: 'center' }}
              color="inverted"
              align="center"
              lineHeight="default"
              fontWeight="medium"
            >
              {title ?? t(`common:signupUpsell.header.${location}`)}
            </TextDynamic>
          </BasicContainer>
          {children ?? (
            <BasicContainer w="full">
              <FlexRow justifyContent="space-between" alignItems="center" w="full" wrap="nowrap">
                <FlexCol
                  textColor="inverted"
                  maxW="1/3"
                  justifyContent="center"
                  alignItems="center"
                  gap="3"
                  className="cn_atom_px-4"
                >
                  <Icon name="tv" />
                  <BodyTextSmall color="lowContrastText" align="center" fontWeight="regular">
                    <Trans
                      i18nKey="common:signupUpsell.newRolesHighlight"
                      components={[<BodyTextSmall color="inverted" align="center" fontWeight="bold" as="span" />]}
                    />
                  </BodyTextSmall>
                </FlexCol>
                <FlexCol minW="md" borderPlacement="right" minHeight={4} justifyContent="center" alignItems="center" />
                <FlexCol
                  textColor="inverted"
                  maxW="1/3"
                  justifyContent="center"
                  alignItems="center"
                  gap="3"
                  className="cn_atom_px-4"
                >
                  <Icon name="award" />
                  <BodyTextSmall color="lowContrastText" align="center" fontWeight="regular">
                    <Trans
                      i18nKey="common:signupUpsell.topCastingPlatform"
                      components={[<BodyTextSmall color="inverted" align="center" fontWeight="bold" as="span" />]}
                    />
                  </BodyTextSmall>
                </FlexCol>
                <FlexCol minW="md" borderPlacement="right" minHeight={4} justifyContent="center" alignItems="center" />
                <FlexCol
                  textColor="inverted"
                  maxW="1/3"
                  justifyContent="center"
                  alignItems="center"
                  gap="3"
                  className="cn_atom_px-2"
                >
                  <Icon name="scene" />
                  <BodyTextSmall color="lowContrastText" align="center" fontWeight="regular">
                    <Trans
                      i18nKey="common:signupUpsell.topProjects"
                      components={[<BodyTextSmall color="inverted" align="center" fontWeight="bold" as="span" />]}
                    />
                  </BodyTextSmall>
                </FlexCol>
              </FlexRow>
            </BasicContainer>
          )}
          {!hideTestimonial && !isMobile && (
            <BasicContainer w="full" mt="8">
              <TestimonialCard fullWidth {...testimonial} textSize="sm" />
            </BasicContainer>
          )}
          {!isMobile && (
            <BasicContainer w="full">
              <TrustedBrands isTransparent showBrands={showBrands} />
            </BasicContainer>
          )}
        </FlexDynamic>
      </FlexDynamic>
    </FlexCol>
  );
};
